import * as React from 'react';
import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';
import { noDefault } from 'lib/eventWrappers';

import { BackPage } from './model';
import { PilotageCharges, Charge, PRICE_PRECISION, BillingStatus } from './pilotagecharges';

import { BillExportSettingsView } from 'portcall/billexportsettings/view';
import { PageLoadingIndicator } from 'core/actionloadingindicator/view';

export const PilotageChargesView = SuperCritical(({ model: m, backPage, flipToFront } : {
    model : PilotageCharges,
    backPage : () => BackPage | null,
    flipToFront : () => void
}) =>
    backPage() === BackPage.PilotageCharges ?
        <div className="pilotage-charges-full">
            <div className="details-section-header">
                <a href="" className="details-section-header-side-item flip-icon icon-flip-to-main"
                    onClick={noDefault(flipToFront)}></a>
                <span className="details-section-title">Pilotage Charges</span>
            </div>
            <PageLoadingIndicator loading={() => m.Loading() || m.BillExportSettings.SendingRequest()} />
            <PilotageChargesBody model={m} />
        </div> : null
);

let PilotageChargesBody = SuperCritical(({ model: m } : { model : PilotageCharges }) =>
    <div className="pilotage-charges-body">
        <BillToView model={m} />
        <BillAttributesView model={m} />
        <BillExportSettingsView model={m.BillExportSettings} />
        <PilotageChargesWatermark model={m} />
        <ChargesListView model={m} />
    </div>
);

let BillToView = SuperCritical(({ model: m } : { model : PilotageCharges }) =>
    <div className="pilotage-charges-bill-to">
        <span className="pc-text-billing-label bill-to-label">Bill To:</span>
        <textarea
            className="bill-to-value"
            value={m.JobOrder.BillToAddress() || ''}
            onChange={e => m.JobOrder.BillToAddress(e.currentTarget.value)}
            // TH 05/04/2017 -- I figure 5 rows can comfortably fit most addresses.
            // If the address has more lines, it'll just scroll.
            rows={5}
            disabled={
                // Matching BillExportSettings' behavior for the QB Customer ID for a
                // consistent UI feel.
                m.BillingStatus() === BillingStatus.NonBillable ||
                m.BillingStatus() === BillingStatus.Approved ||
                m.BillingStatus() === BillingStatus.Exported
            }
        />
    </div>
);

let BillAttributesView = SuperCritical(({ model: m } : { model : PilotageCharges }) =>
    m.Bill() ?
        <div className="pilotage-charges-bill-attributes">
            <div className="attribute-line">
                <span className="attribute-label pc-text-billing-label">Invoice Date:</span>
                <span className="attribute-value">
                    {tzmoment(m.Bill()!.InvoiceDate()).format('M/D/YY')}
                </span>
            </div>
            <div className="attribute-line">
                <span className="attribute-label pc-text-billing-label">Invoice Number:</span>
                <span className="attribute-value">{m.Bill()!.InvoiceNumber()}</span>
            </div>
        </div> : null
);

let PilotageChargesWatermark = SuperCritical(({ model: m } : { model : PilotageCharges }) =>
    <div className="pilotage-charges-watermark">
        {
            m.BillingStatus() === BillingStatus.Exported ? 'Exported' :
            m.BillingStatus() === BillingStatus.Approved ? 'Approved' :
            'Draft Charges'
        }
    </div>
);

let ChargesListView = SuperCritical(({ model: m } : { model : PilotageCharges }) =>
    <div className="charges-list-shell">
        <ChargesListTable model={m} />
        <InvoiceTotalView model={m} />
    </div>
);

let ChargesListTable = SuperCritical(({ model: m } : { model : PilotageCharges }) =>
    <table className="charges-list">
        <thead className="pc-text-billing-label">
            <tr>
                <th className="item-code">Item Code</th>
                <th className="description">Description</th>
                <th className="quantity">Qty.</th>
                <th className="rate">Rate</th>
                <th className="total">Total</th>
            </tr>
        </thead>
        <tbody>
            {m.ChargesList().map(c =>
                <ChargeListItemView charge={c} key={c.Id()} />
            )}
        </tbody>
    </table>
);

let ChargeListItemView = SuperCritical(({ charge: c } : { charge : Charge }) =>
    <tr>
        {/* TH 05/03/2017 -- Using StandardItem.Name instead of the QB-specific QBTracking.Name.
            My thought is that if we support multiple export systems, this represents a common name used
            for display purposes across systems. The actual name used to export to each system may or may
            not be the same. */}
        <td className="item-code">{c.StandardItem() && c.StandardItem()!.Name()}</td>
        <td className="description">{c.Description()}</td>
        <td className="quantity">{c.NumberOfUnits()}</td>
        <td className="rate">{c.UnitPrice.toString({ places: PRICE_PRECISION })}</td>
        <td className="total">{c.Total.toString({ places: PRICE_PRECISION })}</td>
    </tr>
);

let InvoiceTotalView = SuperCritical(({ model: m } : { model : PilotageCharges }) =>
    <div className="invoice-total">
        <span className="pc-text-billing-label invoice-total-label">Invoice Total</span>
        <span>{m.InvoiceTotal.toString({ places: PRICE_PRECISION })}</span>
    </div>
);


// WEBPACK FOOTER //
// ./apps/pilots/pilotjoborderdetails/pilotagechargesview.tsx